import type {
  CoinPackageDefinition,
  DoorDirection,
  FurnitureDefinition,
  FurnitureItemState,
  FriendLink,
  ObjktPagination,
  ObjktToken,
  RoomJoinMode,
  RoomLayoutTile,
  RoomLayoutWallEdge,
  RoomLayoutAccess,
  RoomLayoutOption,
  RoomLayoutState,
  RoomThemeAccess,
  RoomThemeColors,
  RoomThemeOption,
  RoomThemeState,
  RoomSummary,
  UserFurnitureInventoryItem,
  WalletNft,
} from "../../shared/types";

export interface Session {
  address: string;
  displayName: string;
  avatarColor: string;
  isAdmin: boolean;
  token: string;
  tokenExpiresAt: string;
}

export interface SessionCreateResult {
  session: Session;
  isFirstLogin: boolean;
}

export interface RoomWithAccess extends RoomSummary {
  canJoin: boolean;
  hasLayoutAccess: boolean;
  requiresLayoutUnlock: boolean;
  onlineCount: number;
  memberCount: number;
}

export interface RoomJoinDecision {
  allowed: boolean;
  reason: "none" | "members_only" | "password_required" | "token_required";
  canKnock: boolean;
  joinMode: RoomJoinMode;
}

export interface RoomKnock {
  id: string;
  requesterAddress: string;
  message: string;
  status: string;
  createdAt: string;
}

export interface FurnitureInventoryEntry extends UserFurnitureInventoryItem {
  item: FurnitureDefinition;
}

export interface AdminAddress {
  address: string;
  grantedBy?: string;
  createdAt: string;
}

export interface AdminFurnitureInput {
  id?: string;
  name: string;
  description?: string;
  modelUrl: string;
  thumbnailUrl?: string;
  priceCoins: number;
  tintColor?: string;
  artist?: string;
  blockWidth: number;
  blockDepth: number;
  sittable: boolean;
  interactionType?: import("../../shared/types").FurnitureInteractionType;
  walkableTop?: boolean;
  stackableTop?: boolean;
  stackHeight?: number;
  teleportGroup?: string;
  actionOpenAnimation?: string;
  actionCloseAnimation?: string;
  seatOffsetX?: number;
  seatOffsetY?: number;
  seatOffsetZ?: number;
  colorSlots?: string[];
  colorVariants?: import("../../shared/types").FurnitureColorVariant[];
  defaultColorVariantId?: string;
  catalogCategory?: string;
  catalogCollection?: string;
  itemState?: FurnitureItemState;
}

export type AdminFurniturePatchInput = Partial<AdminFurnitureInput>;

export interface AdminFurnitureModelUpload {
  key: string;
  url: string;
  bytes: number;
  checksumSha256: string;
}

export interface AdminFurnitureValidationResult {
  valid: boolean;
  bytes: number;
  checksumSha256: string;
  normalized: Omit<AdminFurnitureInput, "id">;
}

export interface GroupPicture {
  id: string;
  imageData: string;
  takenBy: string;
  createdAt: string;
}

export interface UserProfileSummary {
  address: string;
  displayName: string;
  isAdmin: boolean;
  createdAt: string;
  publishedFurnitureCount: number;
  draftFurnitureCount?: number;
}

export interface UserProfileDetail {
  address: string;
  displayName: string;
  isAdmin: boolean;
  createdAt: string;
  canViewDrafts: boolean;
  furniture: FurnitureDefinition[];
  publishedFurnitureCount: number;
  draftFurnitureCount?: number;
}

export interface ObjktPagedResponse<T> {
  tokens: T[];
  pagination: ObjktPagination;
}

export function wsBase(): string {
  const protocol = window.location.protocol === "https:" ? "wss" : "ws";
  return `${protocol}://${window.location.host}`;
}

function applySessionHeaders(
  headers: Headers,
  session?: Session | null
): void {
  if (session?.token) {
    headers.set("Authorization", `Bearer ${session.token}`);
  } else if (session?.address) {
    headers.set("x-wallet-address", session.address);
  }
}

export async function apiJson<T>(
  url: string,
  options: RequestInit = {},
  session?: Session | null
): Promise<T> {
  const headers = new Headers(options.headers ?? {});
  headers.set("Content-Type", "application/json");
  applySessionHeaders(headers, session);

  const response = await fetch(url, { ...options, headers });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      error?: string;
    };
    throw new Error(
      body.error ?? `Request failed with status ${response.status}`
    );
  }

  return (await response.json()) as T;
}

// --- Auth ---

export interface AuthChallengePayload {
  challengeId: string;
  payloadHex: string;
  issuedAt: string;
  expiresAt: string;
  signingType: "raw" | "micheline" | "operation";
}

export async function createAuthChallenge(
  address: string
): Promise<AuthChallengePayload> {
  return apiJson<AuthChallengePayload>("/api/auth/challenge", {
    method: "POST",
    body: JSON.stringify({ address: address.trim() }),
  });
}

export async function createSession(
  address: string,
  opts: {
    displayName?: string;
    challengeId?: string;
    signature?: string;
    publicKey?: string;
    devBypass?: boolean;
  } = {}
): Promise<SessionCreateResult> {
  const normalizedAddress = address.trim();
  const result = await apiJson<{
    session: {
      address: string;
      displayName: string;
      isAdmin?: boolean;
      token?: string;
      tokenExpiresAt?: string;
    };
    isFirstLogin?: boolean;
  }>("/api/auth/session", {
    method: "POST",
    body: JSON.stringify({
      address: normalizedAddress,
      displayName: opts.displayName,
      challengeId: opts.challengeId,
      signature: opts.signature,
      publicKey: opts.publicKey,
      devBypass: opts.devBypass,
    }),
  });
  if (!result.session.token || !result.session.tokenExpiresAt) {
    throw new Error("Session token missing in response.");
  }
  return {
    session: {
      ...result.session,
      isAdmin: Boolean(result.session.isAdmin),
      avatarColor: "#000000",
      token: result.session.token,
      tokenExpiresAt: result.session.tokenExpiresAt,
    },
    isFirstLogin: Boolean(result.isFirstLogin),
  };
}

export async function updateCurrentProfile(
  session: Session,
  updates: { displayName?: string }
): Promise<{ address: string; displayName: string; isAdmin: boolean }> {
  const result = await apiJson<{
    profile: { address: string; displayName: string; isAdmin: boolean };
  }>(
    "/api/me",
    {
      method: "PATCH",
      body: JSON.stringify(updates),
    },
    session
  );
  return result.profile;
}

// --- Rooms ---

export async function fetchRooms(
  session: Session,
  q?: string
): Promise<RoomWithAccess[]> {
  const params = new URLSearchParams();
  if (q?.trim()) params.set("q", q.trim());
  const qs = params.toString();
  const result = await apiJson<{ rooms: RoomWithAccess[] }>(
    `/api/rooms${qs ? `?${qs}` : ""}`,
    {},
    session
  );
  return result.rooms;
}

export async function updateRoom(
  session: Session,
  roomId: string,
  updates: {
    name?: string;
    isPrivate?: boolean;
    joinMode?: RoomJoinMode;
    joinPassword?: string;
    tokenGateContract?: string;
    allowKnock?: boolean;
    layoutId?: string;
    themeId?: string;
    wallThemeId?: string;
    floorThemeId?: string;
  }
): Promise<void> {
  await apiJson(
    `/api/rooms/${roomId}`,
    {
      method: "PATCH",
      body: JSON.stringify(updates),
    },
    session
  );
}

export async function updateRoomName(
  session: Session,
  roomId: string,
  name: string
): Promise<void> {
  await updateRoom(session, roomId, { name });
}

export async function createRoom(
  session: Session,
  name: string,
  isPrivate: boolean,
  layoutId?: string,
  themeId?: string,
  wallThemeId?: string,
  floorThemeId?: string,
  options?: {
    joinMode?: RoomJoinMode;
    joinPassword?: string;
    tokenGateContract?: string;
    allowKnock?: boolean;
  }
): Promise<RoomWithAccess> {
  const result = await apiJson<{ room: RoomWithAccess }>(
    "/api/rooms",
    {
      method: "POST",
      body: JSON.stringify({
        name,
        isPrivate,
        layoutId,
        themeId,
        wallThemeId,
        floorThemeId,
        joinMode: options?.joinMode,
        joinPassword: options?.joinPassword,
        tokenGateContract: options?.tokenGateContract,
        allowKnock: options?.allowKnock,
      }),
    },
    session
  );
  return result.room;
}

export async function inviteToRoom(
  session: Session,
  roomId: string,
  inviteeAddress: string
): Promise<void> {
  await apiJson(
    `/api/rooms/${roomId}/invite`,
    {
      method: "POST",
      body: JSON.stringify({ inviteeAddress }),
    },
    session
  );
}

export async function deleteRoom(
  session: Session,
  roomId: string
): Promise<void> {
  await apiJson(
    `/api/rooms/${roomId}`,
    {
      method: "DELETE",
    },
    session
  );
}

export async function requestRoomJoin(
  session: Session,
  roomId: string,
  password?: string
): Promise<RoomJoinDecision> {
  return apiJson<RoomJoinDecision>(
    `/api/rooms/${roomId}/join`,
    {
      method: "POST",
      body: JSON.stringify({
        password: password?.trim() || undefined,
      }),
    },
    session
  );
}

export async function knockRoom(
  session: Session,
  roomId: string,
  message?: string
): Promise<{ accepted: boolean; knockId?: string; pending?: boolean }> {
  return apiJson<{ accepted: boolean; knockId?: string; pending?: boolean }>(
    `/api/rooms/${roomId}/knock`,
    {
      method: "POST",
      body: JSON.stringify({
        message: message?.trim() || undefined,
      }),
    },
    session
  );
}

export async function fetchRoomKnocks(
  session: Session,
  roomId: string
): Promise<RoomKnock[]> {
  const result = await apiJson<{ knocks: RoomKnock[] }>(
    `/api/rooms/${roomId}/knocks`,
    {},
    session
  );
  return result.knocks;
}

export async function admitRoomKnock(
  session: Session,
  roomId: string,
  knockId: string
): Promise<void> {
  await apiJson(
    `/api/rooms/${roomId}/knocks/${knockId}/admit`,
    {
      method: "POST",
    },
    session
  );
}

export async function rejectRoomKnock(
  session: Session,
  roomId: string,
  knockId: string
): Promise<void> {
  await apiJson(
    `/api/rooms/${roomId}/knocks/${knockId}/reject`,
    {
      method: "POST",
    },
    session
  );
}

export async function blockUser(
  session: Session,
  roomId: string,
  address: string
): Promise<void> {
  await apiJson(
    `/api/rooms/${roomId}/block`,
    { method: "POST", body: JSON.stringify({ address }) },
    session
  );
}

export async function unblockUser(
  session: Session,
  roomId: string,
  address: string
): Promise<void> {
  await apiJson(
    `/api/rooms/${roomId}/unblock`,
    { method: "POST", body: JSON.stringify({ address }) },
    session
  );
}

export interface RoomLayoutInput {
  name: string;
  maxUsers: number;
  width: number;
  height: number;
  tiles: RoomLayoutTile[][];
  walls?: RoomLayoutWallEdge[];
  door: { x: number; z: number; direction: DoorDirection };
  state: RoomLayoutState;
  access: RoomLayoutAccess;
}

export interface RoomThemeInput {
  name: string;
  state: RoomThemeState;
  access: RoomThemeAccess;
  colors: RoomThemeColors;
}

export async function fetchLayouts(
  session?: Session | null
): Promise<RoomLayoutOption[]> {
  const result = await apiJson<{ layouts: RoomLayoutOption[] }>(
    "/api/layouts",
    {},
    session ?? null
  );
  return result.layouts;
}

export async function fetchRoomThemes(
  session?: Session | null
): Promise<RoomThemeOption[]> {
  const result = await apiJson<{ themes: RoomThemeOption[] }>(
    "/api/room-themes",
    {},
    session ?? null
  );
  return result.themes;
}

export async function fetchAdminLayouts(
  session: Session
): Promise<Array<RoomLayoutOption & { unlockCount: number }>> {
  const result = await apiJson<{
    layouts: Array<RoomLayoutOption & { unlockCount: number }>;
  }>("/api/admin/layouts", {}, session);
  return result.layouts;
}

export async function fetchAdminRoomThemes(
  session: Session
): Promise<Array<RoomThemeOption & { unlockCount: number }>> {
  const result = await apiJson<{
    themes: Array<RoomThemeOption & { unlockCount: number }>;
  }>("/api/admin/room-themes", {}, session);
  return result.themes;
}

export async function createAdminLayout(
  session: Session,
  input: RoomLayoutInput
): Promise<RoomLayoutOption> {
  const result = await apiJson<{ layout: RoomLayoutOption }>(
    "/api/admin/layouts",
    {
      method: "POST",
      body: JSON.stringify(input),
    },
    session
  );
  return result.layout;
}

export async function updateAdminLayout(
  session: Session,
  layoutId: string,
  updates: Partial<RoomLayoutInput>
): Promise<RoomLayoutOption> {
  const result = await apiJson<{ layout: RoomLayoutOption }>(
    `/api/admin/layouts/${layoutId}`,
    {
      method: "PATCH",
      body: JSON.stringify(updates),
    },
    session
  );
  return result.layout;
}

export async function createAdminRoomTheme(
  session: Session,
  input: RoomThemeInput
): Promise<RoomThemeOption> {
  const result = await apiJson<{ theme: RoomThemeOption }>(
    "/api/admin/room-themes",
    {
      method: "POST",
      body: JSON.stringify(input),
    },
    session
  );
  return result.theme;
}

export async function updateAdminRoomTheme(
  session: Session,
  themeId: string,
  updates: Partial<RoomThemeInput>
): Promise<RoomThemeOption> {
  const result = await apiJson<{ theme: RoomThemeOption }>(
    `/api/admin/room-themes/${themeId}`,
    {
      method: "PATCH",
      body: JSON.stringify(updates),
    },
    session
  );
  return result.theme;
}

export async function unlockLayoutForAddress(
  session: Session,
  layoutId: string,
  address: string
): Promise<void> {
  await apiJson(
    `/api/admin/layouts/${layoutId}/unlock`,
    {
      method: "POST",
      body: JSON.stringify({ address }),
    },
    session
  );
}

export async function revokeLayoutUnlock(
  session: Session,
  layoutId: string,
  address: string
): Promise<void> {
  await apiJson(
    `/api/admin/layouts/${layoutId}/unlock/${encodeURIComponent(address)}`,
    {
      method: "DELETE",
    },
    session
  );
}

export async function unlockThemeForAddress(
  session: Session,
  themeId: string,
  address: string
): Promise<void> {
  await apiJson(
    `/api/admin/room-themes/${themeId}/unlock`,
    {
      method: "POST",
      body: JSON.stringify({ address }),
    },
    session
  );
}

export async function revokeThemeUnlock(
  session: Session,
  themeId: string,
  address: string
): Promise<void> {
  await apiJson(
    `/api/admin/room-themes/${themeId}/unlock/${encodeURIComponent(address)}`,
    {
      method: "DELETE",
    },
    session
  );
}

export async function fetchAdmins(session: Session): Promise<AdminAddress[]> {
  const result = await apiJson<{ admins: AdminAddress[] }>(
    "/api/admin/admins",
    {},
    session
  );
  return result.admins;
}

export async function addAdmin(
  session: Session,
  address: string
): Promise<void> {
  await apiJson(
    "/api/admin/admins",
    {
      method: "POST",
      body: JSON.stringify({ address }),
    },
    session
  );
}

export async function removeAdmin(
  session: Session,
  address: string
): Promise<void> {
  await apiJson(
    `/api/admin/admins/${encodeURIComponent(address)}`,
    {
      method: "DELETE",
    },
    session
  );
}

// --- Friends ---

export async function fetchFriends(session: Session): Promise<FriendLink[]> {
  const result = await apiJson<{ friends: FriendLink[] }>(
    "/api/friends",
    {},
    session
  );
  return result.friends;
}

export interface FriendPresence {
  address: string;
  displayName: string | null;
  roomId: string;
  roomName: string;
  roomOwnerName: string | null;
}

export async function fetchFriendsPresence(
  session: Session
): Promise<FriendPresence[]> {
  const result = await apiJson<{ online: FriendPresence[] }>(
    "/api/friends/presence",
    {},
    session
  );
  return result.online;
}

export async function sendFriendRequest(
  session: Session,
  friendAddress: string
): Promise<void> {
  await apiJson(
    "/api/friends/request",
    {
      method: "POST",
      body: JSON.stringify({ friendAddress }),
    },
    session
  );
}

export async function acceptFriendRequest(
  session: Session,
  friendAddress: string
): Promise<void> {
  await apiJson(
    "/api/friends/accept",
    {
      method: "POST",
      body: JSON.stringify({ friendAddress }),
    },
    session
  );
}

export async function removeFriend(
  session: Session,
  friendAddress: string
): Promise<void> {
  await apiJson(
    "/api/friends/remove",
    {
      method: "POST",
      body: JSON.stringify({ friendAddress }),
    },
    session
  );
}

// ── Direct Messages ──────────────────────────────────────

export interface DirectMessage {
  id: string;
  senderAddress: string;
  text: string;
  createdAt: string;
}

export async function sendDM(
  session: Session,
  to: string,
  text: string
): Promise<void> {
  await apiJson(
    "/api/dm",
    { method: "POST", body: JSON.stringify({ to, text }) },
    session
  );
}

export async function fetchDMConversation(
  session: Session,
  friendAddress: string
): Promise<DirectMessage[]> {
  const result = await apiJson<{ messages: DirectMessage[] }>(
    `/api/dm/${friendAddress}`,
    {},
    session
  );
  return result.messages;
}

export async function fetchUnreadDMCount(session: Session): Promise<number> {
  const result = await apiJson<{ count: number }>(
    "/api/dm/unread",
    {},
    session
  );
  return result.count;
}

export async function fetchUnreadPerFriend(
  session: Session
): Promise<Record<string, number>> {
  const result = await apiJson<{ counts: Record<string, number> }>(
    "/api/dm/unread-per-friend",
    {},
    session
  );
  return result.counts;
}

export async function reportPlayer(
  session: Session,
  targetAddress: string,
  roomId?: string,
  reason?: string
): Promise<void> {
  await apiJson(
    "/api/reports",
    {
      method: "POST",
      body: JSON.stringify({ targetAddress, roomId, reason }),
    },
    session
  );
}

// --- Economy & Furniture ---

export async function fetchCoinPackages(): Promise<CoinPackageDefinition[]> {
  const result = await apiJson<{ packages: CoinPackageDefinition[] }>(
    "/api/economy/packages"
  );
  return result.packages;
}

export async function fetchCoinBalance(session: Session): Promise<number> {
  const result = await apiJson<{ coins: number }>(
    "/api/economy/balance",
    {},
    session
  );
  return result.coins;
}

export async function purchaseCoinPackage(
  session: Session,
  packageId: string,
  opHash: string
): Promise<{
  purchased: boolean;
  coins: number;
  package: CoinPackageDefinition;
}> {
  return apiJson<{
    purchased: boolean;
    coins: number;
    package: CoinPackageDefinition;
  }>(
    "/api/economy/purchase",
    {
      method: "POST",
      body: JSON.stringify({
        packageId,
        opHash,
      }),
    },
    session
  );
}

export async function fetchFurnitureCatalog(
  session?: Session | null
): Promise<FurnitureDefinition[]> {
  const result = await apiJson<{ items: FurnitureDefinition[] }>(
    "/api/marketplace/furniture",
    {},
    session ?? null
  );
  return result.items;
}

export async function fetchAdminFurniture(
  session: Session
): Promise<FurnitureDefinition[]> {
  const result = await apiJson<{ items: FurnitureDefinition[] }>(
    "/api/admin/furniture",
    {},
    session
  );
  return result.items;
}

export async function createAdminFurniture(
  session: Session,
  input: AdminFurnitureInput
): Promise<FurnitureDefinition> {
  const result = await apiJson<{ furniture: FurnitureDefinition }>(
    "/api/admin/furniture",
    {
      method: "POST",
      body: JSON.stringify(input),
    },
    session
  );
  return result.furniture;
}

export async function updateAdminFurniture(
  session: Session,
  furnitureId: string,
  input: AdminFurniturePatchInput
): Promise<FurnitureDefinition> {
  const result = await apiJson<{ furniture: FurnitureDefinition }>(
    `/api/admin/furniture/${encodeURIComponent(furnitureId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(input),
    },
    session
  );
  return result.furniture;
}

export async function removeAdminFurniture(
  session: Session,
  furnitureId: string
): Promise<void> {
  await apiJson<{ removed: boolean; furnitureId: string }>(
    `/api/admin/furniture/${encodeURIComponent(furnitureId)}`,
    {
      method: "DELETE",
    },
    session
  );
}

export async function uploadAdminFurnitureModel(
  session: Session,
  file: File,
  metadata: Omit<AdminFurnitureInput, "id" | "modelUrl"> & {
    description?: string;
    thumbnailUrl?: string;
    modelUrl?: string;
  }
): Promise<AdminFurnitureModelUpload> {
  const headers = new Headers();
  applySessionHeaders(headers, session);

  const form = new FormData();
  form.set("file", file, file.name);
  form.set("metadata", JSON.stringify(metadata));

  const response = await fetch("/api/admin/furniture/upload", {
    method: "POST",
    headers,
    body: form,
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      error?: string;
    };
    throw new Error(
      body.error ?? `Upload failed with status ${response.status}`
    );
  }

  const result = (await response.json()) as {
    upload: AdminFurnitureModelUpload;
  };
  return result.upload;
}

export async function validateAdminFurnitureModel(
  session: Session,
  file: File,
  metadata: Omit<AdminFurnitureInput, "id" | "modelUrl"> & {
    description?: string;
    thumbnailUrl?: string;
    modelUrl?: string;
  }
): Promise<AdminFurnitureValidationResult> {
  const headers = new Headers();
  applySessionHeaders(headers, session);

  const form = new FormData();
  form.set("file", file, file.name);
  form.set("metadata", JSON.stringify(metadata));

  const response = await fetch("/api/admin/furniture/validate", {
    method: "POST",
    headers,
    body: form,
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      error?: string;
    };
    throw new Error(
      body.error ?? `Validation failed with status ${response.status}`
    );
  }

  const result = (await response.json()) as {
    validation: AdminFurnitureValidationResult;
  };
  return result.validation;
}

export async function fetchFurnitureInventory(
  session: Session
): Promise<{ coins: number; inventory: FurnitureInventoryEntry[] }> {
  return apiJson<{ coins: number; inventory: FurnitureInventoryEntry[] }>(
    "/api/marketplace/furniture/inventory",
    {},
    session
  );
}

export async function fetchProfiles(
  session: Session
): Promise<UserProfileSummary[]> {
  const result = await apiJson<{ profiles: UserProfileSummary[] }>(
    "/api/profiles",
    {},
    session
  );
  return result.profiles;
}

export async function fetchProfileByAddress(
  session: Session,
  address: string
): Promise<UserProfileDetail> {
  const result = await apiJson<{ profile: UserProfileDetail }>(
    `/api/profiles/${encodeURIComponent(address)}`,
    {},
    session
  );
  return result.profile;
}

export async function buyFurniture(
  session: Session,
  furnitureId: string,
  quantity = 1,
  colorVariantId?: string
): Promise<{
  purchased: boolean;
  furnitureId: string;
  quantity: number;
  coins: number;
  colorVariantId?: string;
}> {
  return apiJson<{
    purchased: boolean;
    furnitureId: string;
    quantity: number;
    coins: number;
    colorVariantId?: string;
  }>(
    "/api/marketplace/furniture/buy",
    {
      method: "POST",
      body: JSON.stringify({
        furnitureId,
        quantity,
        colorVariantId,
      }),
    },
    session
  );
}

// --- Marketplace ---

export async function fetchMarketplace(
  creator?: string,
  limit = 24,
  offset = 0
): Promise<ObjktPagedResponse<ObjktToken>> {
  const query = new URLSearchParams();
  if (creator?.trim()) {
    query.set("creator", creator.trim());
  }
  query.set("limit", String(limit));
  query.set("offset", String(offset));
  const result = await apiJson<ObjktPagedResponse<ObjktToken>>(
    `/api/marketplace/objkt?${query.toString()}`
  );
  return result;
}

export async function searchMarketplaceGlbs(
  queryText: string,
  limit = 24,
  offset = 0
): Promise<ObjktPagedResponse<ObjktToken>> {
  const query = new URLSearchParams({
    q: queryText.trim(),
    limit: String(limit),
    offset: String(offset),
  });
  const result = await apiJson<ObjktPagedResponse<ObjktToken>>(
    `/api/marketplace/objkt?${query.toString()}`
  );
  return result;
}

// --- Held GLBs (tokens the wallet owns) ---

export async function fetchHeldGlbs(
  session: Session,
  limit = 48
): Promise<ObjktToken[]> {
  const result = await fetchHeldGlbsPage(session, { limit, offset: 0 });
  return result.tokens;
}

export async function fetchHeldGlbsPage(
  session: Session,
  options: { limit?: number; offset?: number; search?: string } = {}
): Promise<ObjktPagedResponse<ObjktToken>> {
  const query = new URLSearchParams({
    limit: String(options.limit ?? 48),
    offset: String(options.offset ?? 0),
  });
  if (options.search?.trim()) {
    query.set("q", options.search.trim());
  }
  const result = await apiJson<ObjktPagedResponse<ObjktToken>>(
    `/api/marketplace/objkt/held?${query.toString()}`,
    {},
    session
  );
  return result;
}

export async function fetchHeldNfts(
  session: Session,
  limit = 200
): Promise<WalletNft[]> {
  const result = await fetchHeldNftsPage(session, { limit, offset: 0 });
  return result.tokens;
}

export async function fetchHeldNftsPage(
  session: Session,
  options: { limit?: number; offset?: number; search?: string } = {}
): Promise<ObjktPagedResponse<WalletNft>> {
  const query = new URLSearchParams({
    limit: String(options.limit ?? 200),
    offset: String(options.offset ?? 0),
  });
  if (options.search?.trim()) {
    query.set("q", options.search.trim());
  }
  const result = await apiJson<ObjktPagedResponse<WalletNft>>(
    `/api/marketplace/objkt/held-all?${query.toString()}`,
    {},
    session
  );
  return result;
}

// --- Token Balance ---

export async function fetchTokenBalance(
  session: Session,
  faContract: string,
  tokenId: string
): Promise<number> {
  const query = new URLSearchParams({
    fa_contract: faContract,
    token_id: tokenId,
  });
  const result = await apiJson<{ balance: number }>(
    `/api/marketplace/objkt/balance?${query.toString()}`,
    {},
    session
  );
  return result.balance;
}

// --- Pictures ---

export async function fetchPictures(roomId: string): Promise<GroupPicture[]> {
  const result = await apiJson<{ pictures: GroupPicture[] }>(
    `/api/pictures/${roomId}`
  );
  return result.pictures;
}

export async function savePicture(
  session: Session,
  roomId: string,
  imageData: string,
  metadata: Record<string, unknown>
): Promise<void> {
  await apiJson(
    "/api/pictures",
    {
      method: "POST",
      body: JSON.stringify({ roomId, imageData, metadata }),
    },
    session
  );
}
